import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreControllerInput } from '@ingenico/react-ui/core-form';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextMaxLength, TitleMaxLength } from '../block-device-modal-form/useBlockDeviceForm.vm';
import type { BlockDeviceModalProps } from './BlockDeviceModal.model';
import styles from './BlockDeviceModal.module.scss';
import { useBlockDeviceModalVm } from './useBlockDeviceModal.vm';

export function BlockDeviceModal({ isOpen, isLoading, onBlockDevice, onCancel }: BlockDeviceModalProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const { form } = useBlockDeviceModalVm();

  return isConfirmModal ? (
    <BasicDialogModal
      actions={
        <div className={styles.modalActions}>
          <CoreButton disabled={isLoading} onClick={() => setIsConfirmModal(false)} variant="text">
            {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          </CoreButton>
          <CoreButton
            loading={isLoading}
            onClick={() => {
              setIsConfirmModal(false);
              void onBlockDevice(form.getValues());
            }}
            variant="contained"
          >
            {t('button.blockDevice', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
          </CoreButton>
        </div>
      }
      customPaperClassName={styles.paperContainer}
      customActionsClassName={styles.customActionsClassName}
      isOpen={isOpen}
      onClose={onCancel}
      title={t('blockDeviceModal.confirmationTitle', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
      subTitle={t('blockDeviceModal.confirmationSubtitle', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
    />
  ) : (
    <BasicDialogModal
      actions={
        <div className={styles.modalActions}>
          <CoreButton disabled={isLoading} onClick={onCancel} variant="text" className={styles.cancelButton}>
            {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          </CoreButton>
          <CoreButton
            loading={isLoading}
            disabled={!form.formState.isValid}
            onClick={() => {
              setIsConfirmModal(true);
            }}
            variant="contained"
            className={styles.saveButton}
          >
            {t('button.blockDevice', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
          </CoreButton>
        </div>
      }
      customPaperClassName={styles.paperContainer}
      customActionsClassName={styles.customActionsClassName}
      hasDivider
      isOpen={isOpen}
      onClose={onCancel}
      title={t('blockDeviceModal.blockTitle', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
      subTitle={t('blockDeviceModal.blockSubtitle', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
    >
      <div className={styles.form__container}>
        <div className={styles.nameAndDescriptionContainer}>
          <CoreControllerInput
            control={form.control}
            dataTestId="field-block-device-title"
            label={t('blockDeviceModal.fields.title')}
            name="title"
            maxLength={TitleMaxLength}
            required
          />
          <CoreControllerInput
            control={form.control}
            dataTestId="field-block-device-text"
            label={t('blockDeviceModal.fields.text')}
            multiline
            maxLength={TextMaxLength}
            name="text"
            required
          />
        </div>
      </div>
    </BasicDialogModal>
  );
}
